<template>
    <v-dialog :value="value" width="500" @input="$emit('input', $event)">
        <v-card component="BibleDialog">
            <btn class="close" size="icon" color="transparent" :icon="'close'" @click="close" />

            <h3 class="title">{{ title }}</h3>

            <form @submit.prevent="save">
                <div class="input">
                    <label>{{ isBook ? 'BOOK NAME' : 'CHAPTER NAME' }}</label>
                    <input ref="caption" v-model="form.caption" name="caption" type="text" />
                    <span class="invalid" v-if="err.caption">{{ err.caption[0] }}</span>
                </div>

                <div class="input" v-if="!isBook">
                    <v-select
                        v-model="form.id_book"
                        :items="books"
                        class="select"
                        hide-details
                        item-text="caption"
                        item-value="id"
                        label="Book"
                    />
                    <span class="invalid" v-if="err.id_book">{{ err.id_book[0] }}</span>
                </div>

                <p class="message" v-if="message">{{ message }}</p>

                <div class="action">
                    <template v-if="form.id">
                        <template v-if="usage">
                            <span class="usage">{{ usage }}</span>
                        </template>
                        <span v-else-if="confirmDelete" class="confirm">
                            Really delete?
                            <a class="button" color="red" @click.prevent="remove"><span>YES</span></a>
                            <a class="button" @click.prevent="confirmDelete = false"><span>NO</span></a>
                        </span>
                        <a v-else class="button" @click.prevent="confirmDelete = true">
                            <i class="icon">delete</i>
                            <span>DELETE</span>
                        </a>
                    </template>

                    <a class="button save" color="base" @click.prevent="save">
                        <i class="icon">save</i>
                        <span>SAVE</span>
                    </a>
                </div>
            </form>
        </v-card>
    </v-dialog>
</template>

<script>
import { ABibleApi } from '@/api';

export default {
    name: 'BibleDialog',

    props: {
        value: { type: Boolean, default: false },
        // 'book' | 'chapter'
        type: { type: String, required: true },
        record: { type: Object, default: null },
        books: { type: Array, default: () => [] },
        // preselected book for a new chapter
        defaultBook: { type: Number, default: null },
    },

    data: () => ({
        form: {},
        err: {},
        message: '',
        saving: false,
        confirmDelete: false,
    }),

    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (!open) {
                    return;
                }

                this.form = this.record
                    ? { ...this.record }
                    : { id: null, caption: '', id_book: this.isBook ? undefined : this.defaultBook };
                this.err = {};
                this.message = '';
                this.confirmDelete = false;

                this.$nextTick(() => this.$refs.caption && this.$refs.caption.focus());
            },
        },
    },

    methods: {
        close() {
            this.$emit('input', false);
        },

        async save() {
            if (this.saving) {
                return;
            }
            this.saving = true;

            try {
                const { data } = this.isBook
                    ? await ABibleApi.saveBook(this.form)
                    : await ABibleApi.saveChapter(this.form);
                this.$emit('saved', data.record);
                this.close();
            } catch (error) {
                this.err = error?.response?.data?.data || {};
                this.message = Object.keys(this.err).length ? '' : 'Saving failed.';
            } finally {
                this.saving = false;
            }
        },

        async remove() {
            try {
                this.isBook ? await ABibleApi.deleteBook(this.form.id) : await ABibleApi.deleteChapter(this.form.id);
                this.$emit('deleted', this.form);
                this.close();
            } catch (error) {
                this.confirmDelete = false;
                this.message = error?.response?.data?.message || 'Deleting failed.';
            }
        },
    },

    computed: {
        isBook() {
            return this.type === 'book';
        },

        title() {
            const what = this.isBook ? 'book' : 'chapter';
            return this.form.id ? `Edit ${what}` : `New ${what}`;
        },

        // records in use cannot be deleted
        usage() {
            if (this.isBook && this.form.chapters_count) {
                return `Has ${this.form.chapters_count} chapters, cannot be deleted.`;
            }
            if (!this.isBook && this.form.actors_count) {
                return `Used by ${this.form.actors_count} actors, cannot be deleted.`;
            }
            return '';
        },
    },
};
</script>

<style lang="less" scoped>
@import '~@variables';

[component='BibleDialog'] {
    padding: 20px;

    .close {
        position: absolute;
        top: 0;
        right: 0;
        margin: 0;
    }

    .title {
        margin: 0 30px 20px 0;
        text-transform: uppercase;
    }

    form {
        display: flex;
        flex-direction: column;
        gap: 10px;

        input[type='text'] {
            width: 100%;
            border-width: 0 0 1px 0;
            border-style: solid;
            border-color: @grey;
        }
    }

    .input span.invalid {
        top: 100% !important;
    }

    .message {
        color: @color_magenta;
        margin: 0;
    }

    .action {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 20px;

        .usage {
            font-size: 12px;
            color: #888;
        }

        .confirm {
            display: flex;
            align-items: center;
            gap: 6px;
            font-weight: 700;
        }

        .save {
            margin-left: auto;
        }
    }
}
</style>
